import type { ButtonProps } from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { PermissionUtils } from '@tyro/api';
import { ChevronRightIcon } from '@tyro/icons';
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import { Link, type LinkProps } from 'react-router';
import { MenuTransition } from './menu-transition';
import { useSubmenuIntent } from './use-submenu-hover-intent';

export interface MenuItemConfig {
  label: string;
  icon?: React.ReactNode;
  indent?: boolean;
  shortcut?: string;
  subMenu?: MenuItemConfig[];
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  navigateTo?: LinkProps['to'];
  selected?: boolean;
  state?: LinkProps['state'];
  onBeforeNavigate?: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
  hasAccess?: (permissions: PermissionUtils) => boolean;
  isDelete?: boolean;
  'aria-expanded'?: ButtonProps['aria-expanded'];
  'aria-controls'?: ButtonProps['aria-controls'];
}

interface MenuItemListProps {
  menuItems: MenuItemConfig[];
  onClose: () => void;
  permissions: PermissionUtils;
}

export const ActionMenuIconWrapper = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: 10,
  display: 'inline-flex',
  alignItems: 'center',
  '& svg': {
    width: 20,
    height: 20,
  },
}));

const ActionMenuIndentSpacer = styled('span')({
  display: 'inline-block',
  width: 20,
  marginRight: 10,
});

export function getMenuItemList({
  menuItems,
  permissions,
  onClose,
}: MenuItemListProps) {
  return menuItems
    .filter((menuItem) => menuItem.hasAccess?.(permissions) ?? true)
    .map((item) => (
      <ActionMenuItem
        key={item.label}
        item={item}
        onClose={onClose}
        disabled={item.disabled}
        selected={item.selected}
      />
    ));
}

interface ActionMenuItemProps {
  item: MenuItemConfig;
  onClose: () => void;
  disabled?: boolean;
  selected?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
}

function ActionMenuItem({
  item,
  onClose,
  disabled: _ignoredDisabled,
  selected: _ignoredSelected,
  ...menuListProps
}: ActionMenuItemProps) {
  const {
    label,
    icon,
    indent,
    shortcut,
    subMenu,
    navigateTo,
    state,
    selected,
    onBeforeNavigate,
    onClick,
    disabled,
    disabledTooltip,
    isDelete,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
  } = item;

  let leadingAdornment: React.ReactNode = null;
  if (icon) {
    leadingAdornment = <ActionMenuIconWrapper>{icon}</ActionMenuIconWrapper>;
  } else if (indent) {
    leadingAdornment = <ActionMenuIndentSpacer />;
  }

  const intent = useSubmenuIntent();
  const itemRef = useRef<HTMLLIElement>(null);
  const generatedId = useId();
  const submenuId = subMenu ? generatedId : null;
  const isSubmenuOpen = submenuId !== null && intent?.openId === submenuId;

  let rightAdornment: React.ReactNode = null;
  if (subMenu) {
    rightAdornment = (
      <ChevronRightIcon
        sx={{ ml: 'auto', mr: -1.125, color: 'text.disabled' }}
      />
    );
  } else if (shortcut) {
    rightAdornment = (
      <Typography
        component="span"
        variant="caption"
        sx={{ ml: 'auto', pl: 4, color: 'text.disabled' }}
      >
        {shortcut}
      </Typography>
    );
  }

  const setActiveSubmenu = intent?.setActiveSubmenu;
  const clearActiveSubmenu = intent?.clearActiveSubmenu;
  useEffect(() => {
    if (!submenuId || !subMenu) return;
    if (isSubmenuOpen) {
      setActiveSubmenu?.({
        id: submenuId,
        anchorEl: itemRef.current,
        items: subMenu,
        onClose,
      });
    } else {
      clearActiveSubmenu?.(submenuId);
    }
  }, [
    isSubmenuOpen,
    submenuId,
    subMenu,
    onClose,
    setActiveSubmenu,
    clearActiveSubmenu,
  ]);

  const openSubmenu = () => {
    if (submenuId) intent?.forceOpen(submenuId);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (!submenuId) return;
    if (
      event.key === 'ArrowRight' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      openSubmenu();
    }
  };

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    event.currentTarget.focus();
    intent?.requestSwitchTo(submenuId);
  };

  const Item = (
    <MenuItem
      ref={itemRef}
      key={label}
      selected={selected || isSubmenuOpen}
      {...menuListProps}
      {...(navigateTo && !subMenu
        ? {
            component: Link,
            to: navigateTo,
            state,
            onClick: onBeforeNavigate,
          }
        : {
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              if (subMenu) {
                openSubmenu();
                return;
              }
              onClick?.(event as React.MouseEvent<HTMLButtonElement>);
              onClose();
            },
            onKeyDown: handleKeyDown,
            'aria-haspopup': subMenu ? 'menu' : undefined,
            'aria-expanded': subMenu ? isSubmenuOpen : ariaExpanded,
            'aria-controls': subMenu ? (submenuId ?? undefined) : ariaControls,
          })}
      onMouseEnter={handleMouseEnter}
      disabled={disabled}
      sx={({ palette }) => ({
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: palette.action.hover,
        },
        ...(isDelete
          ? {
              '&.Mui-selected': {
                backgroundColor: palette.rose[500_16],
              },
              '&.Mui-selected:hover, &.Mui-focusVisible, &:focus': {
                backgroundColor: palette.rose[500_8],
                color: palette.rose[600],
                '& span': {
                  color: palette.rose[600],
                },
              },
              '& .MuiTouchRipple-child': {
                backgroundColor: palette.rose[500],
              },
            }
          : {}),
      })}
    >
      {leadingAdornment} {label}
      {rightAdornment}
    </MenuItem>
  );

  return disabledTooltip && disabled ? (
    <Tooltip describeChild title={disabledTooltip} disableInteractive>
      <span>{Item}</span>
    </Tooltip>
  ) : (
    Item
  );
}

interface SubMenuProps {
  submenuId: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  items: MenuItemConfig[];
  permissions: PermissionUtils;
  onClose: () => void;
}

export function SubMenu({
  submenuId,
  anchorEl,
  open,
  items,
  permissions,
  onClose,
}: SubMenuProps) {
  const theme = useTheme();
  const intent = useSubmenuIntent();
  const setAim = intent?.setAim;

  const handlePaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!setAim) return;
      setAim(node && anchorEl ? { submenuEl: node, parentEl: anchorEl } : null);
    },
    [anchorEl, setAim],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      intent?.close(submenuId);
      anchorEl?.focus();
    }
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      transition
      style={{ zIndex: theme.zIndex.modal + 1 }}
      modifiers={[
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', options: { fallbackPlacements: ['left-start'] } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
    >
      {({ TransitionProps, placement }) => (
        <MenuTransition
          {...TransitionProps}
          style={{
            transformOrigin: placement.startsWith('left')
              ? 'right top'
              : 'left top',
          }}
        >
          <Paper
            ref={handlePaperRef}
            onMouseEnter={() => intent?.cancelScheduledClose()}
            sx={{
              boxShadow: theme.customShadows?.dropdown ?? theme.shadows[8],
              borderRadius: 1.75,
              minWidth: 160,
            }}
          >
            <ClickAwayListener onClickAway={() => intent?.close(submenuId)}>
              <MenuList
                autoFocusItem={open}
                onKeyDown={handleKeyDown}
                role="menu"
                sx={{ py: 0.75 }}
              >
                {getMenuItemList({ menuItems: items, permissions, onClose })}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </MenuTransition>
      )}
    </Popper>
  );
}
